// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** manifestGate — the PURE enforcement primitives for the 0.2a CAPABILITY MANIFEST
 *  (CONTRACT-C3 "Proposed evolution"; docs/AOA-ACTOR-ACCESS-MODEL.md §3a + §2).
 *
 *  Two responsibilities, both side-effect-free + deterministic so they are provable
 *  in isolation (the capability-host test suite):
 *   1. MANIFEST OVER-REACH — decide whether a single contribution (kind + command id
 *      + declared actors) is WITHIN what the manifest permits. The host refuses an
 *      over-reaching contribution (registers nothing for it) and logs, while keeping
 *      the capability LIVE (the crash-isolation contract is preserved).
 *   2. TYPED-INPUT VALIDATION — validate a machine-supplied payload against a command's
 *      declared `input` JSON-Schema-ish shape BEFORE run() in host dispatch.
 *
 *  FAIL-CLOSED: an unparseable manifest / permission / schema is treated as a DENY,
 *  never defaulted-open (AOA §1; the object-plane rule mirrored onto the action plane).
 *
 *  LEGACY-PERMISSIVE: these primitives apply ONLY when a manifest (resp. a typed
 *  `input` / `permission` / `risk`) is present. A manifest-less registration / an
 *  untyped `{ id, title, run }` command is UNGATED — so every existing caller and the
 *  40/40 lockstep are unaffected (additive by construction). */

import type {
  CapabilityManifest,
  ActorClass,
  ContributionKindName,
} from "../../contracts-ts/src/index.ts";

/** The verdict of a manifest over-reach check. `allowed:false` ⇒ the host MUST refuse
 *  the contribution (register nothing) and log `reason`; the capability stays live. */
export interface ManifestVerdict {
  allowed: boolean;
  /** Human-readable refusal reason (logged); empty when allowed. */
  reason: string;
}

const ALLOW: ManifestVerdict = { allowed: true, reason: "" };
function refuse(reason: string): ManifestVerdict {
  return { allowed: false, reason };
}

/** Is `kind` a contribution kind the manifest declares? PURE. A malformed
 *  `contributes` (not an array) ⇒ refuse everything (fail-closed). */
export function manifestAllowsKind(
  manifest: CapabilityManifest,
  kind: ContributionKindName,
): ManifestVerdict {
  const declared = manifest.contributes;
  if (!Array.isArray(declared)) {
    return refuse(`manifest.contributes is malformed (fail-closed)`);
  }
  if (!declared.includes(kind)) {
    return refuse(
      `contribution kind "${kind}" is not declared in the manifest ` +
        `(declared: [${declared.join(", ")}])`,
    );
  }
  return ALLOW;
}

/** Decide whether a COMMAND contribution (its id + the actors the CommandSpec asks
 *  for) is within the manifest. Beyond the kind check, the manifest may constrain WHO
 *  may invoke a command id via `commandActors[id]`. A command whose declared `actors`
 *  WIDEN the manifest's allow-list (introduce a class the manifest does not grant) is
 *  refused. An id ABSENT from `commandActors` is unconstrained by the manifest (it
 *  keeps its own `actors`). FAIL-CLOSED on a malformed allow-list. PURE. */
export function manifestAllowsCommand(
  manifest: CapabilityManifest,
  commandId: string,
  specActors: readonly ActorClass[] | undefined,
): ManifestVerdict {
  const kindVerdict = manifestAllowsKind(manifest, "command");
  if (!kindVerdict.allowed) return kindVerdict;

  const map = manifest.commandActors;
  if (map === undefined) return ALLOW;
  if (typeof map !== "object" || map === null) {
    return refuse(`manifest.commandActors is malformed (fail-closed)`);
  }
  if (!Object.prototype.hasOwnProperty.call(map, commandId)) {
    // The manifest does not constrain this command id ⇒ no added constraint.
    return ALLOW;
  }
  const allowed = map[commandId];
  if (!Array.isArray(allowed)) {
    return refuse(
      `manifest.commandActors["${commandId}"] is malformed (fail-closed)`,
    );
  }
  // The CommandSpec must not widen beyond the manifest: every class the spec asks for
  // must be in the manifest's allow-list. (Absent spec actors ⇒ the command inherits
  // exactly the manifest's set — never wider — so that is allowed.)
  for (const a of specActors ?? []) {
    if (!allowed.includes(a)) {
      return refuse(
        `command "${commandId}" declares actor "${a}" not granted by the manifest ` +
          `(manifest grants: [${allowed.join(", ")}]) — over-reach refused`,
      );
    }
  }
  return ALLOW;
}

// --- Typed-input validation ---------------------------------------------------

/** The outcome of validating a machine-supplied payload against a typed `input`. */
export interface ValidationResult {
  valid: boolean;
  /** First validation error (host raises it); empty when valid. */
  error: string;
}

const VALID: ValidationResult = { valid: true, error: "" };
function invalid(error: string): ValidationResult {
  return { valid: false, error };
}

/** Validate `payload` against a command's declared `input` schema. We support the
 *  minimal JSON-Schema object surface the emitter also speaks (type:"object" +
 *  properties + required + additionalProperties + a per-property primitive `type`) —
 *  enough to gate a machine-supplied payload without a schema-lib dependency.
 *
 *  - No declared schema (undefined/null) ⇒ the command is untyped ⇒ legacy-permissive:
 *    nothing to validate, ALWAYS valid (the caller decides not to call this at all for
 *    such commands).
 *  - A schema that is not an object-typed JSON Schema ⇒ FAIL-CLOSED (we will not
 *    silently accept a payload we cannot check).
 *  PURE: never mutates payload or schema. */
export function validateInput(schema: unknown, payload: unknown): ValidationResult {
  if (schema === undefined || schema === null) return VALID;
  if (typeof schema !== "object" || Array.isArray(schema)) {
    return invalid(`declared input schema is not a JSON-Schema object (fail-closed)`);
  }
  const s = schema as Record<string, unknown>;
  if (s.type !== "object") {
    return invalid(`declared input schema.type must be "object" (fail-closed)`);
  }
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
    return invalid(`payload must be a JSON object`);
  }
  const obj = payload as Record<string, unknown>;
  const props = (s.properties as Record<string, unknown>) ?? {};
  const required = Array.isArray(s.required) ? (s.required as unknown[]) : [];

  // Required keys must be present.
  for (const key of required) {
    if (typeof key !== "string" || !(key in obj)) {
      return invalid(`missing required property "${String(key)}"`);
    }
  }
  // additionalProperties:false ⇒ no key outside the declared property set.
  if (s.additionalProperties === false) {
    for (const key of Object.keys(obj)) {
      if (!(key in props)) {
        return invalid(`unexpected property "${key}" (additionalProperties:false)`);
      }
    }
  }
  // Per-property primitive type check (string/number/boolean/object/array), when the
  // property schema declares one. Absent/unknown declared type ⇒ skip (not stricter
  // than the schema says).
  for (const [key, propSchemaUnknown] of Object.entries(props)) {
    if (!(key in obj)) continue;
    const propSchema = propSchemaUnknown as Record<string, unknown>;
    const declaredType = propSchema?.type;
    if (typeof declaredType !== "string") continue;
    if (!matchesPrimitive(obj[key], declaredType)) {
      return invalid(
        `property "${key}" expected type "${declaredType}", got "${jsonType(obj[key])}"`,
      );
    }
  }
  return VALID;
}

/** The JSON-Schema-ish type name for a value. */
function jsonType(v: unknown): string {
  if (v === null) return "null";
  if (Array.isArray(v)) return "array";
  return typeof v; // "string" | "number" | "boolean" | "object" | ...
}

function matchesPrimitive(v: unknown, declaredType: string): boolean {
  switch (declaredType) {
    case "string":
      return typeof v === "string";
    case "number":
    case "integer":
      return typeof v === "number" && Number.isFinite(v);
    case "boolean":
      return typeof v === "boolean";
    case "array":
      return Array.isArray(v);
    case "object":
      return typeof v === "object" && v !== null && !Array.isArray(v);
    case "null":
      return v === null;
    default:
      // Unknown declared type ⇒ do not block (no stricter than the schema).
      return true;
  }
}
